import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  FileOutlined,
  HomeOutlined,
  LinkedinOutlined,
  PrinterOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Empty,
  Layout,
  Modal,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import CustomizableResumePreview from "@/Components/Common/CustomizableResumePreview";
import { logger } from "@/Services/Logger/Index";
import { sortResumeForDisplay } from "@/Utils/DateSortingUtils";
import { loadCustomSectionOrder } from "@/Utils/SectionOrderingUtils";
import PrintableResume from "./PrintableResume";

// Helper function to format dates to MM/YYYY format
const formatDateToMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr === "Present" || dateStr === "Current" || dateStr === "present" || dateStr === "current") {
    return "Present";
  }

  try {
    // Try to parse various date formats
    let date;

    // Check if already in MM/YYYY or MM-YYYY format
    if (/^\d{2}[-/]\d{4}$/.test(dateStr)) {
      return dateStr.replace("-", "/"); // Normalize to MM/YYYY
    }

    // Check if it's a year only (YYYY)
    if (/^\d{4}$/.test(dateStr)) {
      return `01/${dateStr}`; // Default to January
    }

    // Try to parse the date
    date = new Date(dateStr);

    if (date && !isNaN(date.getTime())) {
      // Format as MM/YYYY
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${year}`;
    }

    // If all parsing fails, return original string
    return dateStr;
  } catch (error) {
    console.warn("Date formatting error:", dateStr, error);
    return dateStr;
  }
};

const { Content } = Layout;
const { Title, Text } = Typography;

// Template Card Component
const TemplateCard = ({ template, isSelected, onSelect, onPreview, resumeData }) => {
  const renderPreviewImage = () => {
    const { parsedData } = resumeData || {};
    const sortedData = parsedData ? sortResumeForDisplay(parsedData) : null;

    switch (template.id) {
      case "classic":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "16px",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Times, serif",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "10px",
                borderBottom: "1px solid #333",
                paddingBottom: "6px",
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                {sortedData?.personalInfo?.name || "Your Name"}
              </div>
              <div style={{ fontSize: "8px", color: "#666" }}>
                {[
                  sortedData?.personalInfo?.email,
                  sortedData?.personalInfo?.phone,
                  sortedData?.personalInfo?.location,
                  sortedData?.personalInfo?.linkedIn,
                  sortedData?.personalInfo?.github,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </div>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  borderBottom: "1px solid #333",
                  marginBottom: "4px",
                }}
              >
                SUMMARY
              </div>
              <div style={{ fontSize: "7px" }}>
                {sortedData?.summary
                  ? typeof sortedData.summary === "string"
                    ? sortedData.summary.substring(0, 60) + "..."
                    : (sortedData.summary.content || "").substring(0, 60) + "..."
                  : "Professional summary..."}
              </div>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  borderBottom: "1px solid #333",
                  marginBottom: "4px",
                }}
              >
                EXPERIENCE
              </div>
              <div style={{ fontSize: "8px", fontWeight: "bold" }}>
                {sortedData?.experience?.[0]?.title || "Position Title"}
              </div>
              <div style={{ fontSize: "7px", color: "#666" }}>
                {sortedData?.experience?.[0]?.company || "Company Name"} •{" "}
                {formatDateToMMYYYY(sortedData?.experience?.[0]?.startDate) || "Year"}
              </div>
            </div>
            <div style={{ marginBottom: "6px" }}>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "bold",
                  borderBottom: "1px solid #333",
                  marginBottom: "4px",
                }}
              >
                SKILLS
              </div>
              <div style={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
                {(() => {
                  const skills = sortedData?.skills;
                  if (Array.isArray(skills)) return skills;
                  if (skills?.list) return skills.list;
                  if (skills?.categorized) {
                    return [
                      ...(skills.categorized.technical || []),
                      ...(skills.categorized.soft || []),
                      ...(skills.categorized.tools || []),
                      ...(skills.categorized.other || []),
                    ];
                  }
                  return [];
                })()
                  .slice(0, 2)
                  .map((skill, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: "#f0f0f0",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        fontSize: "6px",
                      }}
                    >
                      {skill}
                    </span>
                  )) || [
                  <span
                    key={0}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "2px 4px",
                      borderRadius: "3px",
                      fontSize: "6px",
                    }}
                  >
                    Skills
                  </span>,
                ]}
              </div>
            </div>
            {sortedData?.projects && sortedData.projects.length > 0 && (
              <div style={{ marginBottom: "6px" }}>
                <div
                  style={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    borderBottom: "1px solid #333",
                    marginBottom: "2px",
                  }}
                >
                  PROJECTS
                </div>
                <div style={{ fontSize: "6px", fontWeight: "bold" }}>{sortedData.projects[0].name}</div>
              </div>
            )}
            {sortedData?.certifications && sortedData.certifications.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    borderBottom: "1px solid #333",
                    marginBottom: "2px",
                  }}
                >
                  CERTIFICATIONS
                </div>
                <div style={{ fontSize: "6px", fontWeight: "bold" }}>{sortedData.certifications[0].name}</div>
              </div>
            )}
            {sortedData?.achievements && sortedData.achievements.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    borderBottom: "1px solid #333",
                    marginBottom: "2px",
                  }}
                >
                  ACHIEVEMENTS
                </div>
                {sortedData.achievements.slice(0, 2).map((achievement, index) => (
                  <div key={index} style={{ fontSize: "6px", marginBottom: "1px" }}>
                    • {achievement.description?.split("\n")[0]?.substring(0, 50)}...
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case "modern":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
            }}
          >
            <div style={{ backgroundColor: "#2563eb", color: "white", padding: "12px", marginBottom: "8px" }}>
              <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                {sortedData?.personalInfo?.name || "Your Name"}
              </div>
              <div style={{ fontSize: "7px", opacity: "0.9" }}>
                {[
                  sortedData?.personalInfo?.email,
                  sortedData?.personalInfo?.phone,
                  sortedData?.personalInfo?.location,
                  sortedData?.personalInfo?.linkedIn,
                  sortedData?.personalInfo?.github,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </div>
            </div>
            <div style={{ padding: "0 12px", display: "grid", gridTemplateColumns: "1fr 2fr", gap: "8px" }}>
              <div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2px solid #2563eb",
                    paddingLeft: "6px",
                    marginBottom: "4px",
                  }}
                >
                  Skills
                </div>
                {(() => {
                  const skills = sortedData?.skills;
                  if (Array.isArray(skills)) return skills;
                  if (skills?.list) return skills.list;
                  if (skills?.categorized) {
                    return [
                      ...(skills.categorized.technical || []),
                      ...(skills.categorized.soft || []),
                      ...(skills.categorized.tools || []),
                      ...(skills.categorized.other || []),
                    ];
                  }
                  return [];
                })()
                  .slice(0, 2)
                  .map((skill, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: "#f1f5f9",
                        padding: "3px 6px",
                        borderRadius: "8px",
                        fontSize: "6px",
                        textAlign: "center",
                        marginBottom: "3px",
                      }}
                    >
                      {skill}
                    </div>
                  )) || [
                  <div
                    key={0}
                    style={{
                      backgroundColor: "#f1f5f9",
                      padding: "3px 6px",
                      borderRadius: "8px",
                      fontSize: "6px",
                      textAlign: "center",
                      marginBottom: "3px",
                    }}
                  >
                    Skills
                  </div>,
                ]}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2px solid #2563eb",
                    paddingLeft: "6px",
                    marginBottom: "4px",
                  }}
                >
                  Experience
                </div>
                <div style={{ fontSize: "8px", fontWeight: "bold", color: "#374151" }}>
                  {sortedData?.experience?.[0]?.title || "Position Title"}
                </div>
                <div style={{ fontSize: "7px", color: "#6b7280", marginBottom: "3px" }}>
                  {sortedData?.experience?.[0]?.company || "Company Name"}
                </div>
                {sortedData?.experience && sortedData.experience.length > 1 && (
                  <div style={{ marginTop: "3px" }}>
                    <div style={{ fontSize: "7px", fontWeight: "bold", color: "#374151" }}>
                      {sortedData.experience[1].title || "Previous Role"}
                    </div>
                    <div style={{ fontSize: "6px", color: "#6b7280", marginBottom: "6px" }}>
                      {sortedData.experience[1].company || "Previous Company"}
                    </div>
                  </div>
                )}
                {sortedData?.projects && sortedData.projects.length > 0 && (
                  <div style={{ marginBottom: "4px" }}>
                    <div
                      style={{
                        fontSize: "7px",
                        color: "#2563eb",
                        fontWeight: "bold",
                        borderLeft: "2px solid #2563eb",
                        paddingLeft: "4px",
                        marginBottom: "2px",
                      }}
                    >
                      Projects
                    </div>
                    <div style={{ fontSize: "6px", color: "#374151" }}>{sortedData.projects[0].name}</div>
                    {sortedData.projects.length > 1 && (
                      <div style={{ fontSize: "5px", color: "#6b7280", marginTop: "1px" }}>
                        + {sortedData.projects.length - 1} more projects
                      </div>
                    )}
                  </div>
                )}
                {sortedData?.certifications && sortedData.certifications.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: "7px",
                        color: "#2563eb",
                        fontWeight: "bold",
                        borderLeft: "2px solid #2563eb",
                        paddingLeft: "4px",
                        marginBottom: "2px",
                      }}
                    >
                      Certifications
                    </div>
                    <div style={{ fontSize: "6px", color: "#374151" }}>{sortedData.certifications[0].name}</div>
                    {sortedData.certifications.length > 1 && (
                      <div style={{ fontSize: "5px", color: "#6b7280", marginTop: "1px" }}>
                        + {sortedData.certifications.length - 1} more certs
                      </div>
                    )}
                  </div>
                )}
                {sortedData?.achievements && sortedData.achievements.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: "7px",
                        color: "#2563eb",
                        fontWeight: "bold",
                        borderLeft: "2px solid #2563eb",
                        paddingLeft: "4px",
                        marginBottom: "2px",
                      }}
                    >
                      Achievements
                    </div>
                    {sortedData.achievements.slice(0, 2).map((achievement, index) => (
                      <div key={index} style={{ fontSize: "6px", color: "#374151", marginBottom: "1px" }}>
                        • {achievement.description?.split("\n")[0]?.substring(0, 40)}...
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case "creative":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "4px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "6px",
                left: "6px",
                right: "6px",
                bottom: "6px",
                backgroundColor: "rgba(255,255,255,0.95)",
                borderRadius: "8px",
                padding: "8px",
                fontSize: "8px",
                lineHeight: "1.2",
                fontFamily: "Georgia, serif",
              }}
            >
              <div style={{ textAlign: "center", marginBottom: "6px" }}>
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    margin: "0 auto 4px auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {(sortedData?.personalInfo?.name || "Y").charAt(0).toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: "8px",
                    fontWeight: "300",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {sortedData?.personalInfo?.name || "Your Name"}
                </div>
                <div style={{ fontSize: "5px", color: "#666", fontStyle: "italic" }}>
                  {[
                    sortedData?.personalInfo?.email,
                    sortedData?.personalInfo?.phone,
                    sortedData?.personalInfo?.location,
                    sortedData?.personalInfo?.linkedIn,
                    sortedData?.personalInfo?.github,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                </div>
              </div>
              <div
                style={{
                  padding: "6px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "6px",
                  marginBottom: "4px",
                  border: "1px solid #667eea",
                }}
              >
                <div style={{ fontSize: "6px", color: "#374151", marginBottom: "2px", fontWeight: "300" }}>
                  About Me
                </div>
                <div style={{ fontSize: "4px", color: "#4b5563", fontStyle: "italic" }}>
                  {sortedData?.summary
                    ? typeof sortedData.summary === "string"
                      ? sortedData.summary.substring(0, 40) + "..."
                      : (sortedData.summary.content || "").substring(0, 40) + "..."
                    : "Professional summary..."}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px", marginBottom: "4px" }}>
                <div
                  style={{
                    padding: "4px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "6px",
                    border: "1px solid #f59e0b",
                  }}
                >
                  <div style={{ fontSize: "5px", color: "#92400e", textAlign: "center", marginBottom: "2px" }}>
                    Skills
                  </div>
                  <div
                    style={{
                      backgroundColor: "#fbbf24",
                      color: "white",
                      padding: "1px 3px",
                      borderRadius: "4px",
                      fontSize: "4px",
                      textAlign: "center",
                    }}
                  >
                    {(() => {
                      const skills = sortedData?.skills;
                      if (Array.isArray(skills)) return skills[0] || "Skills";
                      if (skills?.list) return skills.list[0] || "Skills";
                      if (skills?.categorized) {
                        const allSkills = [
                          ...(skills.categorized.technical || []),
                          ...(skills.categorized.soft || []),
                          ...(skills.categorized.tools || []),
                          ...(skills.categorized.other || []),
                        ];
                        return allSkills[0] || "Skills";
                      }
                      return "Skills";
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    padding: "4px",
                    backgroundColor: "#ecfdf5",
                    borderRadius: "6px",
                    border: "1px solid #10b981",
                  }}
                >
                  <div style={{ fontSize: "5px", color: "#047857", textAlign: "center", marginBottom: "2px" }}>
                    Education
                  </div>
                  <div style={{ fontSize: "4px", color: "#065f46" }}>
                    {sortedData?.education?.[0]?.degree || "Education"}
                  </div>
                  {sortedData?.education && sortedData.education.length > 1 && (
                    <div style={{ fontSize: "3px", color: "#065f46", opacity: "0.7", marginTop: "1px" }}>
                      + {sortedData.education.length - 1} more
                    </div>
                  )}
                </div>
              </div>
              {(sortedData?.projects?.length > 0 ||
                sortedData?.certifications?.length > 0 ||
                sortedData?.achievements?.length > 0) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px" }}>
                  {sortedData?.projects && sortedData.projects.length > 0 && (
                    <div
                      style={{
                        padding: "4px",
                        backgroundColor: "#fdf4ff",
                        borderRadius: "6px",
                        border: "1px solid #c084fc",
                      }}
                    >
                      <div style={{ fontSize: "5px", color: "#7c3aed", textAlign: "center", marginBottom: "2px" }}>
                        Projects
                      </div>
                      <div style={{ fontSize: "4px", color: "#581c87" }}>{sortedData.projects[0].name}</div>
                      {sortedData.projects.length > 1 && (
                        <div style={{ fontSize: "3px", color: "#581c87", opacity: "0.7", marginTop: "1px" }}>
                          + {sortedData.projects.length - 1} more
                        </div>
                      )}
                    </div>
                  )}
                  {sortedData?.certifications && sortedData.certifications.length > 0 && (
                    <div
                      style={{
                        padding: "4px",
                        backgroundColor: "#fef7ff",
                        borderRadius: "6px",
                        border: "1px solid #f472b6",
                      }}
                    >
                      <div style={{ fontSize: "5px", color: "#be185d", textAlign: "center", marginBottom: "2px" }}>
                        Certifications
                      </div>
                      <div style={{ fontSize: "4px", color: "#831843" }}>{sortedData.certifications[0].name}</div>
                      {sortedData.certifications.length > 1 && (
                        <div style={{ fontSize: "3px", color: "#831843", opacity: "0.7", marginTop: "1px" }}>
                          + {sortedData.certifications.length - 1} more
                        </div>
                      )}
                    </div>
                  )}
                  {sortedData?.achievements && sortedData.achievements.length > 0 && (
                    <div
                      style={{
                        padding: "4px",
                        backgroundColor: "#fff7ed",
                        borderRadius: "6px",
                        border: "1px solid #fb923c",
                      }}
                    >
                      <div style={{ fontSize: "5px", color: "#ea580c", textAlign: "center", marginBottom: "2px" }}>
                        Achievements
                      </div>
                      <div style={{ fontSize: "4px", color: "#9a3412" }}>
                        • {sortedData.achievements[0].description?.split("\n")[0]?.substring(0, 30)}...
                      </div>
                      {sortedData.achievements.length > 1 && (
                        <div style={{ fontSize: "3px", color: "#9a3412", opacity: "0.7", marginTop: "1px" }}>
                          + {sortedData.achievements.length - 1} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      case "simple_blush":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {/* White header with blush border */}
            <div
              style={{
                backgroundColor: "#ffffff",
                padding: "10px 12px",
                borderBottom: "2px solid #e8b4b8",
              }}
            >
              <div style={{ fontSize: "11px", fontWeight: "bold", color: "#2d3748" }}>
                {sortedData?.personalInfo?.name || "Your Name"}
              </div>
              <div style={{ fontSize: "7px", color: "#718096", marginTop: "2px" }}>
                {sortedData?.experience?.[0]?.title || "Professional Title"}
              </div>
              <div style={{ fontSize: "6px", color: "#718096", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                <span>Location</span>
                <span style={{ width: "3px", height: "3px", borderRadius: "50%", backgroundColor: "#e8b4b8" }}></span>
                <span>email@example.com</span>
              </div>
            </div>
            <div style={{ padding: "8px 12px" }}>
              {/* Experience section */}
              <div style={{ marginBottom: "8px" }}>
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: "bold",
                    color: "#2d3748",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #e2e8f0",
                    paddingBottom: "2px",
                    marginBottom: "4px",
                  }}
                >
                  Professional Experience
                </div>
                <div style={{ fontSize: "8px", fontWeight: "bold", color: "#2d3748" }}>
                  {sortedData?.experience?.[0]?.title || "Position Title"}
                </div>
                <div style={{ fontSize: "7px", color: "#718096", fontStyle: "italic" }}>
                  {sortedData?.experience?.[0]?.company || "Company Name"}
                </div>
              </div>
              {/* Skills with blush bars */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div>
                  <div
                    style={{
                      fontSize: "8px",
                      fontWeight: "bold",
                      color: "#2d3748",
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    Skills
                  </div>
                  {(() => {
                    const skills = sortedData?.skills;
                    let skillsList = [];
                    if (Array.isArray(skills)) skillsList = skills;
                    else if (skills?.list) skillsList = skills.list;
                    else if (skills?.categorized) {
                      skillsList = [
                        ...(skills.categorized.technical || []),
                        ...(skills.categorized.soft || []),
                      ];
                    }
                    return skillsList.slice(0, 3).map((skill, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "3px",
                          gap: "4px",
                        }}
                      >
                        <div style={{ fontSize: "6px", width: "40px", color: "#2d3748" }}>{skill}</div>
                        <div
                          style={{
                            flex: 1,
                            height: "3px",
                            backgroundColor: "#e2e8f0",
                            borderRadius: "2px",
                          }}
                        >
                          <div
                            style={{
                              height: "3px",
                              width: `${85 - index * 15}%`,
                              backgroundColor: "#e8b4b8",
                              borderRadius: "2px",
                            }}
                          />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "8px",
                      fontWeight: "bold",
                      color: "#2d3748",
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    Languages
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "3px",
                      gap: "4px",
                    }}
                  >
                    <div style={{ fontSize: "6px", width: "35px", color: "#2d3748" }}>English</div>
                    <div
                      style={{
                        flex: 1,
                        height: "3px",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "2px",
                      }}
                    >
                      <div
                        style={{
                          height: "3px",
                          width: "100%",
                          backgroundColor: "#e8b4b8",
                          borderRadius: "2px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "modern_slate":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
              position: "relative",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "12px 12px 8px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1, paddingRight: "8px" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#1e293b" }}>
                  {sortedData?.personalInfo?.name || "Your Name"}
                </div>
                <div style={{ fontSize: "7px", color: "#64748b", fontStyle: "italic", marginTop: "2px" }}>
                  {sortedData?.experience?.[0]?.title || "Professional Title"}
                </div>
                {/* Contact grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 6px", marginTop: "6px" }}>
                  {[
                    { icon: "✉", text: "email@example.com" },
                    { icon: "📞", text: "+1 234 567" },
                    { icon: "📍", text: "Location" },
                    { icon: "📅", text: "DOB" },
                  ].map((item, index) => (
                    <div key={index} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#f1f5f9",
                          borderRadius: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "5px",
                        }}
                      >
                        {item.icon}
                      </div>
                      <span style={{ fontSize: "5px", color: "#475569" }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Profile photo placeholder */}
              <div
                style={{
                  width: "35px",
                  height: "35px",
                  borderRadius: "50%",
                  backgroundColor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  color: "#64748b",
                }}
              >
                👤
              </div>
            </div>
            {/* Content */}
            <div style={{ padding: "0 12px" }}>
              {/* Profile Section */}
              <div style={{ marginBottom: "6px" }}>
                <div
                  style={{
                    fontSize: "7px",
                    fontWeight: "bold",
                    color: "#ffffff",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    backgroundColor: "#6a7991",
                    padding: "3px 6px",
                    marginBottom: "4px",
                    textAlign: "center",
                  }}
                >
                  Profile
                </div>
                <div style={{ fontSize: "6px", color: "#475569", lineHeight: "1.4" }}>
                  {sortedData?.summary?.slice(0, 80) || "Professional summary text here..."}
                </div>
              </div>
              {/* Experience Section */}
              <div>
                <div
                  style={{
                    fontSize: "7px",
                    fontWeight: "bold",
                    color: "#ffffff",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    backgroundColor: "#6a7991",
                    padding: "3px 6px",
                    marginBottom: "4px",
                    textAlign: "center",
                  }}
                >
                  Experience
                </div>
                <div style={{ fontSize: "7px", fontWeight: "bold", color: "#1e293b" }}>
                  {sortedData?.experience?.[0]?.company || "Company Name"}
                </div>
                <div style={{ fontSize: "6px", color: "#64748b", fontStyle: "italic" }}>
                  {sortedData?.experience?.[0]?.title || "Position Title"}
                </div>
              </div>
            </div>
          </div>
        );
      case "professional_blue":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Georgia, serif",
              padding: "10px",
            }}
          >
            {/* Header with centered name */}
            <div style={{ textAlign: "center", marginBottom: "8px", borderBottom: "2px solid #3b5998", paddingBottom: "8px" }}>
              <div style={{ fontSize: "10px", fontStyle: "italic", color: "#2c3e50" }}>
                {sortedData?.personalInfo?.name}
              </div>
              {/* Contact icons row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "4px",
                  fontSize: "5px",
                  color: "#555",
                }}
              >
                {sortedData?.personalInfo?.location && <span>📍 {sortedData.personalInfo.location}</span>}
                {sortedData?.personalInfo?.email && <span>✉ {sortedData.personalInfo.email}</span>}
                {sortedData?.personalInfo?.phone && <span>📞 {sortedData.personalInfo.phone}</span>}
              </div>
            </div>
            {/* Summary */}
            {sortedData?.summary && (
              <div style={{ fontSize: "5px", color: "#444", marginBottom: "6px", textAlign: "justify" }}>
                {sortedData.summary.slice(0, 80)}...
              </div>
            )}
            {/* Experience Section */}
            {sortedData?.experience?.length > 0 && (
              <div style={{ marginBottom: "6px" }}>
                <div style={{ marginBottom: "4px" }}>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                  <div
                    style={{
                      fontSize: "6px",
                      fontWeight: "bold",
                      color: "#3b5998",
                      textAlign: "center",
                      padding: "3px 0",
                    }}
                  >
                    Professional Experience
                  </div>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                </div>
                <div style={{ display: "flex", fontSize: "5px" }}>
                  {/* LEFT (25%) - Job info, date, location */}
                  <div style={{ width: "25%", paddingRight: "6px" }}>
                    <div style={{ fontWeight: "bold", color: "#2c3e50" }}>
                      {sortedData.experience[0]?.title},
                    </div>
                    <div style={{ color: "#666", fontStyle: "italic" }}>
                      {sortedData.experience[0]?.company}
                    </div>
                    {sortedData.experience[0]?.startDate && (
                      <div style={{ color: "#666" }}>{sortedData.experience[0].startDate}</div>
                    )}
                    {sortedData.experience[0]?.location && (
                      <div style={{ color: "#666" }}>{sortedData.experience[0].location}</div>
                    )}
                  </div>
                  {/* RIGHT (75%) - Bullet points */}
                  <div style={{ width: "75%" }}>
                    {sortedData.experience[0]?.description && (
                      <div style={{ color: "#444" }}>
                        • {sortedData.experience[0].description.slice(0, 100)}...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Skills Section */}
            {sortedData?.skills?.length > 0 && (
              <div style={{ marginBottom: "6px" }}>
                <div style={{ marginBottom: "3px" }}>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                  <div
                    style={{
                      fontSize: "6px",
                      fontWeight: "bold",
                      color: "#3b5998",
                      textAlign: "center",
                      padding: "3px 0",
                    }}
                  >
                    Skills
                  </div>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                </div>
                <div style={{ fontSize: "5px", color: "#2c3e50", textAlign: "left" }}>
                  {sortedData.skills.slice(0, 5).map((skill, i) => (
                    <span key={i}>
                      {typeof skill === "string" ? skill : skill.name}
                      {i < Math.min(sortedData.skills.length, 5) - 1 && " • "}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Languages Section */}
            {sortedData?.languages?.length > 0 && (
              <div>
                <div style={{ marginBottom: "3px" }}>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                  <div
                    style={{
                      fontSize: "6px",
                      fontWeight: "bold",
                      color: "#3b5998",
                      textAlign: "center",
                      padding: "3px 0",
                    }}
                  >
                    Languages
                  </div>
                  <div style={{ height: "1px", backgroundColor: "#3b5998", width: "100%" }} />
                </div>
                <div style={{ display: "flex", gap: "3px", justifyContent: "flex-start" }}>
                  {sortedData.languages.slice(0, 3).map((lang, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: "#3b5998",
                        color: "white",
                        fontSize: "5px",
                        padding: "2px 6px",
                        borderRadius: "1px",
                      }}
                    >
                      {typeof lang === "string" ? lang : lang.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case "obsidian_edge":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {/* Dark obsidian header */}
            <div
              style={{
                backgroundColor: "#1a1a1a",
                padding: "10px 12px",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: "bold", color: "#ffffff", letterSpacing: "0.5px", marginBottom: "6px" }}>
                {sortedData?.personalInfo?.name || "Mateo Vargas"}
              </div>
              {/* Contact grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "5px", color: "#e0e0e0" }}>✉ {sortedData?.personalInfo?.email || "email@example.com"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "5px", color: "#e0e0e0" }}>📞 {sortedData?.personalInfo?.phone || "402-555-0182"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "5px", color: "#e0e0e0" }}>📍 {sortedData?.personalInfo?.location || "Lincoln, NE"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "5px", color: "#e0e0e0" }}>🔗 github.com/profile</span>
                </div>
              </div>
            </div>
            {/* Content */}
            <div style={{ padding: "6px 12px" }}>
              {/* Profile Section */}
              <div style={{ marginBottom: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px" }}>
                  <div style={{ width: "10px", height: "10px", backgroundColor: "#1a1a1a", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "5px", color: "#ffffff" }}>👤</div>
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a" }}>Profile</span>
                </div>
                <div style={{ fontSize: "5px", color: "#444444", lineHeight: "1.4" }}>
                  {sortedData?.summary
                    ? typeof sortedData.summary === "string"
                      ? sortedData.summary.substring(0, 100) + "..."
                      : (sortedData.summary.content || "").substring(0, 100) + "..."
                    : "Senior Software Engineer with expertise in full-stack development..."}
                </div>
              </div>
              {/* Experience Section */}
              <div style={{ marginBottom: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px" }}>
                  <div style={{ width: "10px", height: "10px", backgroundColor: "#1a1a1a", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "5px", color: "#ffffff" }}>💼</div>
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a" }}>Professional Experience</span>
                </div>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#1a1a1a" }}>
                  {sortedData?.experience?.[0]?.title || "Senior Software Engineer"}, {sortedData?.experience?.[0]?.company || "Company Name"}
                </div>
                <div style={{ fontSize: "5px", color: "#444444", marginTop: "1px" }}>
                  • {sortedData?.experience?.[0]?.description?.substring(0, 60) || "Built scalable applications..."}
                </div>
              </div>
              {/* Education Section */}
              <div style={{ marginBottom: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px" }}>
                  <div style={{ width: "10px", height: "10px", backgroundColor: "#1a1a1a", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "5px", color: "#ffffff" }}>🎓</div>
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a" }}>Education</span>
                </div>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#1a1a1a" }}>
                  {sortedData?.education?.[0]?.degree || "Master of Science"}
                </div>
                <div style={{ fontSize: "5px", color: "#666666" }}>
                  {sortedData?.education?.[0]?.school || "University Name"}
                </div>
              </div>
              {/* Skills Section */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px" }}>
                  <div style={{ width: "10px", height: "10px", backgroundColor: "#1a1a1a", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "5px", color: "#ffffff" }}>✓</div>
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a" }}>Skills</span>
                </div>
                <div style={{ fontSize: "5px", color: "#1a1a1a" }}>
                  {(() => {
                    const skills = sortedData?.skills;
                    if (Array.isArray(skills)) return skills.slice(0, 6).join(" • ");
                    if (skills?.list) return skills.list.slice(0, 6).join(" • ");
                    if (skills?.categorized) {
                      const allSkills = [
                        ...(skills.categorized.technical || []),
                        ...(skills.categorized.soft || []),
                      ];
                      return allSkills.slice(0, 6).join(" • ");
                    }
                    return "TypeScript • React • Node.js • MongoDB • AWS • Docker";
                  })()}
                </div>
              </div>
            </div>
          </div>
        );
      case "clean_professional":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
              padding: "10px",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", paddingBottom: "6px", borderBottom: "1px solid #000", marginBottom: "8px" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>{sortedData?.personalInfo?.name || "Your Name"}</div>
              <div style={{ fontSize: "8px", fontStyle: "italic" }}>{sortedData?.experience?.[0]?.title || sortedData?.experience?.[0]?.jobTitle || "Professional Title"}</div>
            </div>
            {/* Contact */}
            <div style={{ display: "flex", marginBottom: "8px" }}>
              <div style={{ width: "25%", fontSize: "5px", fontWeight: "bold", textTransform: "uppercase" }}>Contact</div>
              <div style={{ width: "75%", borderLeft: "1px solid #e5e5e5", paddingLeft: "6px", fontSize: "4px" }}>
                {sortedData?.personalInfo?.phone && <><strong>Phone:</strong> {sortedData.personalInfo.phone}</>}
                {sortedData?.personalInfo?.phone && sortedData?.personalInfo?.email && " | "}
                {sortedData?.personalInfo?.email && <><strong>Email:</strong> {sortedData.personalInfo.email}</>}
              </div>
            </div>
            {/* Experience */}
            {sortedData?.experience?.length > 0 && (
              <div style={{ display: "flex", marginBottom: "8px" }}>
                <div style={{ width: "25%", fontSize: "5px", fontWeight: "bold", textTransform: "uppercase" }}>Experience</div>
                <div style={{ width: "75%", borderLeft: "1px solid #e5e5e5", paddingLeft: "6px" }}>
                  <div style={{ fontSize: "6px", fontWeight: "bold" }}>
                    {sortedData.experience[0]?.title || sortedData.experience[0]?.jobTitle} | {formatDateToMMYYYY(sortedData.experience[0]?.startDate)}
                  </div>
                  <div style={{ fontSize: "4px" }}>{sortedData.experience[0]?.company}</div>
                </div>
              </div>
            )}
            {/* Education */}
            {sortedData?.education?.length > 0 && (
              <div style={{ display: "flex" }}>
                <div style={{ width: "25%", fontSize: "5px", fontWeight: "bold", textTransform: "uppercase" }}>Education</div>
                <div style={{ width: "75%", borderLeft: "1px solid #e5e5e5", paddingLeft: "6px" }}>
                  <div style={{ fontSize: "6px", fontWeight: "bold" }}>
                    {sortedData.education[0]?.school} | {formatDateToMMYYYY(sortedData.education[0]?.startDate)}-{formatDateToMMYYYY(sortedData.education[0]?.endDate)}
                  </div>
                  <div style={{ fontSize: "4px" }}>{sortedData.education[0]?.degree}</div>
                </div>
              </div>
            )}
          </div>
        );
      case "minimalist_pro":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {/* Header with teal top accent bar */}
            <div
              style={{
                borderTop: "4px solid #0d9488",
                padding: "10px 12px 8px 12px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "14px", fontWeight: "bold", color: "#111827" }}>
                    {sortedData?.personalInfo?.name || "Your Name"}
                  </div>
                  <div style={{ fontSize: "8px", color: "#0d9488", fontWeight: "500", marginTop: "2px" }}>
                    {sortedData?.experience?.[0]?.title || sortedData?.experience?.[0]?.jobTitle || "Professional Title"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "5px", color: "#6b7280" }}>{sortedData?.personalInfo?.email || "email@example.com"}</div>
                  <div style={{ fontSize: "5px", color: "#6b7280" }}>{sortedData?.personalInfo?.phone || "(555) 123-4567"}</div>
                </div>
              </div>
            </div>
            {/* Content */}
            <div style={{ padding: "0 12px" }}>
              {/* Profile Section */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <div style={{ width: "2px", height: "10px", backgroundColor: "#0d9488", borderRadius: "1px", marginRight: "6px" }} />
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#111827", textTransform: "uppercase", letterSpacing: "0.5px" }}>Profile</span>
                </div>
                <div style={{ fontSize: "5px", color: "#4b5563", lineHeight: "1.4" }}>
                  {sortedData?.summary
                    ? typeof sortedData.summary === "string"
                      ? sortedData.summary.substring(0, 100) + "..."
                      : (sortedData.summary.content || "").substring(0, 100) + "..."
                    : "Professional summary..."}
                </div>
              </div>
              {/* Experience Section */}
              {sortedData?.experience?.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                    <div style={{ width: "2px", height: "10px", backgroundColor: "#0d9488", borderRadius: "1px", marginRight: "6px" }} />
                    <span style={{ fontSize: "7px", fontWeight: "bold", color: "#111827", textTransform: "uppercase", letterSpacing: "0.5px" }}>Experience</span>
                  </div>
                  <div style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: "4px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: "7px", fontWeight: "bold", color: "#111827" }}>
                          {sortedData.experience[0]?.title || sortedData.experience[0]?.jobTitle}
                        </div>
                        <div style={{ fontSize: "6px", color: "#0d9488", fontWeight: "500" }}>
                          {sortedData.experience[0]?.company}
                        </div>
                      </div>
                      <div style={{ fontSize: "5px", color: "#9ca3af" }}>
                        {formatDateToMMYYYY(sortedData.experience[0]?.startDate)} – Present
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Skills Section with pill style */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <div style={{ width: "2px", height: "10px", backgroundColor: "#0d9488", borderRadius: "1px", marginRight: "6px" }} />
                  <span style={{ fontSize: "7px", fontWeight: "bold", color: "#111827", textTransform: "uppercase", letterSpacing: "0.5px" }}>Skills</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                  {(() => {
                    const skills = sortedData?.skills;
                    if (Array.isArray(skills)) return skills;
                    if (skills?.list) return skills.list;
                    if (skills?.categorized) {
                      return [
                        ...(skills.categorized.technical || []),
                        ...(skills.categorized.soft || []),
                        ...(skills.categorized.tools || []),
                        ...(skills.categorized.other || []),
                      ];
                    }
                    return [];
                  })()
                    .slice(0, 6)
                    .map((skill, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: "#f0fdfa",
                          border: "1px solid #99f6e4",
                          borderRadius: "8px",
                          padding: "1px 5px",
                          fontSize: "5px",
                          color: "#0d9488",
                          fontWeight: "500",
                        }}
                      >
                        {skill}
                      </div>
                    )) || [
                    <div
                      key={0}
                      style={{
                        backgroundColor: "#f0fdfa",
                        border: "1px solid #99f6e4",
                        borderRadius: "8px",
                        padding: "1px 5px",
                        fontSize: "5px",
                        color: "#0d9488",
                      }}
                    >
                      Skills
                    </div>,
                  ]}
                </div>
              </div>
              {/* Education Section */}
              {sortedData?.education?.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                    <div style={{ width: "2px", height: "10px", backgroundColor: "#0d9488", borderRadius: "1px", marginRight: "6px" }} />
                    <span style={{ fontSize: "7px", fontWeight: "bold", color: "#111827", textTransform: "uppercase", letterSpacing: "0.5px" }}>Education</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: "7px", fontWeight: "bold", color: "#111827" }}>
                        {sortedData.education[0]?.degree}
                      </div>
                      <div style={{ fontSize: "6px", color: "#6b7280" }}>
                        {sortedData.education[0]?.school}
                      </div>
                    </div>
                    <div style={{ fontSize: "5px", color: "#9ca3af" }}>
                      {formatDateToMMYYYY(sortedData.education[0]?.startDate)} – {formatDateToMMYYYY(sortedData.education[0]?.endDate)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case "photo_classic":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
              padding: "10px 12px",
            }}
          >
            {/* Header with photo on LEFT */}
            <div
              style={{
                display: "flex",
                marginBottom: "10px",
              }}
            >
              {/* Profile photo placeholder on LEFT - square with initials */}
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "4px",
                  backgroundColor: "#f5f5f5",
                  marginRight: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: "500",
                  color: "#9ca3af",
                  flexShrink: 0,
                  border: "1px solid #e5e5e5",
                }}
              >
                {(sortedData?.personalInfo?.firstName?.charAt(0) || sortedData?.personalInfo?.name?.charAt(0) || "S")}
                {(sortedData?.personalInfo?.lastName?.charAt(0) || sortedData?.personalInfo?.name?.split(" ")[1]?.charAt(0) || "S")}
              </div>
              {/* Header info on RIGHT */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#1a1a1a", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {sortedData?.personalInfo?.name || "SHARYA SINGH"}
                </div>
                <div style={{ fontSize: "7px", color: "#666666", marginTop: "2px" }}>
                  {sortedData?.workExperience?.[0]?.jobTitle || sortedData?.experience?.[0]?.title || "Web Designer"}
                </div>
                <div style={{ fontSize: "5px", color: "#444444", marginTop: "4px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <span>📞 {sortedData?.personalInfo?.phone || "+123-456-7890"}</span>
                  <span>✉️ {sortedData?.personalInfo?.email || "hello@example.com"}</span>
                </div>
              </div>
            </div>
            {/* Content */}
            <div>
              {/* About Me Section */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px", marginBottom: "4px" }}>
                  ABOUT ME
                </div>
                <div style={{ fontSize: "5px", color: "#333333", lineHeight: "1.5" }}>
                  {typeof sortedData?.summary === "string"
                    ? sortedData.summary.substring(0, 100) + "..."
                    : "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore..."}
                </div>
              </div>
              {/* Education Section */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px", marginBottom: "4px" }}>
                  EDUCATION
                </div>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "30%", fontSize: "5px", color: "#666666" }}>
                    {sortedData?.education?.[0]?.startDate ? formatDateToMMYYYY(sortedData.education[0].startDate) : "2020"} - {sortedData?.education?.[0]?.endDate ? formatDateToMMYYYY(sortedData.education[0].endDate) : "2023"}
                    <div style={{ marginTop: "1px" }}>University</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "6px", fontWeight: "bold", color: "#1a1a1a" }}>
                      {sortedData?.education?.[0]?.degree || "Master of IT Management"}
                    </div>
                    <div style={{ fontSize: "5px", color: "#666666", marginTop: "1px" }}>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                    </div>
                  </div>
                </div>
              </div>
              {/* Skills Section */}
              <div>
                <div style={{ fontSize: "7px", fontWeight: "bold", color: "#1a1a1a", textTransform: "uppercase", letterSpacing: "1px", borderBottom: "1px solid #e5e5e5", paddingBottom: "2px", marginBottom: "4px" }}>
                  SKILLS
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {["Web Design Tools", "UI/UX Design", "Color Theory", "Version Control", "Front-End"].map((skill, index) => (
                    <div key={index} style={{ width: "48%", fontSize: "5px", color: "#444444" }}>
                      • {skill}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case "executive_sidebar":
        const firstName = sortedData?.personalInfo?.firstName || sortedData?.personalInfo?.name?.split(" ")[0] || "Yash";
        const lastName = sortedData?.personalInfo?.lastName || sortedData?.personalInfo?.name?.split(" ").slice(1).join(" ") || "Koradiya";
        const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              overflow: "hidden",
              fontSize: "9px",
              lineHeight: "1.3",
              fontFamily: "Arial, sans-serif",
              display: "flex",
            }}
          >
            {/* Sidebar - Dark blue */}
            <div
              style={{
                width: "35%",
                backgroundColor: "#2d3e50",
                padding: "10px 8px",
              }}
            >
              {/* Profile Photo Placeholder - Circular */}
              <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#4a5568",
                    border: "2px solid #ffffff",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#ffffff",
                  }}
                >
                  {initials}
                </div>
              </div>
              {/* Contact Section */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #4a5c6e", paddingBottom: "2px", marginBottom: "4px" }}>
                  Contact
                </div>
                <div style={{ fontSize: "4px", color: "#d1d5db", marginBottom: "2px" }}>
                  {sortedData?.personalInfo?.phone || "(957) 453-7645"}
                </div>
                <div style={{ fontSize: "4px", color: "#d1d5db", marginBottom: "2px" }}>
                  {sortedData?.personalInfo?.email || "email@example.com"}
                </div>
                <div style={{ fontSize: "4px", color: "#d1d5db" }}>
                  {sortedData?.personalInfo?.location || "Ahmedabad, Gujarat"}
                </div>
              </div>
              {/* Education Section */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #4a5c6e", paddingBottom: "2px", marginBottom: "4px" }}>
                  Education
                </div>
                <div style={{ fontSize: "5px", fontWeight: "bold", color: "#ffffff" }}>
                  {sortedData?.education?.[0]?.degree || "B.E. Computer Science"}
                </div>
                <div style={{ fontSize: "4px", color: "#d1d5db" }}>
                  {sortedData?.education?.[0]?.school || "Govt. Engineering College"}
                </div>
              </div>
              {/* Skills Section with Progress Bars */}
              <div>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #4a5c6e", paddingBottom: "2px", marginBottom: "4px" }}>
                  Skills
                </div>
                {["React.js", "JavaScript", "Node.js"].map((skill, index) => (
                  <div key={index} style={{ marginBottom: "3px" }}>
                    <div style={{ fontSize: "4px", color: "#d1d5db", marginBottom: "1px" }}>{skill}</div>
                    <div style={{ height: "2px", backgroundColor: "#4a5c6e", borderRadius: "1px" }}>
                      <div style={{ height: "2px", backgroundColor: "#60a5fa", borderRadius: "1px", width: `${70 + index * 10}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Main Content - Right */}
            <div
              style={{
                width: "65%",
                padding: "10px 8px",
              }}
            >
              {/* Header with Name */}
              <div style={{ borderBottom: "2px solid #2d3e50", paddingBottom: "6px", marginBottom: "8px" }}>
                <div style={{ fontSize: "10px", fontWeight: "bold", color: "#2d3e50", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {firstName}
                </div>
                <div style={{ fontSize: "10px", fontWeight: "300", color: "#2d3e50", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {lastName}
                </div>
                <div style={{ fontSize: "5px", color: "#6b7280", letterSpacing: "0.3px", marginTop: "2px" }}>
                  {sortedData?.workExperience?.[0]?.jobTitle || sortedData?.experience?.[0]?.title || "Full Stack Developer"}
                </div>
              </div>
              {/* About Me */}
              <div style={{ marginBottom: "6px" }}>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#2d3e50", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #e5e7eb", paddingBottom: "2px", marginBottom: "3px" }}>
                  About Me
                </div>
                <div style={{ fontSize: "4px", color: "#4b5563", lineHeight: "1.4" }}>
                  {typeof sortedData?.summary === "string"
                    ? sortedData.summary.substring(0, 80) + "..."
                    : "Passionate Full Stack Developer with expertise in React.js and Node.js..."}
                </div>
              </div>
              {/* Experience */}
              <div style={{ marginBottom: "6px" }}>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#2d3e50", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #e5e7eb", paddingBottom: "2px", marginBottom: "3px" }}>
                  Experience
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "5px", fontWeight: "bold", color: "#1f2937" }}>
                      {sortedData?.workExperience?.[0]?.company || sortedData?.experience?.[0]?.company || "Webrex Studio"}
                    </div>
                    <div style={{ fontSize: "4px", color: "#6b7280", fontStyle: "italic" }}>
                      {sortedData?.workExperience?.[0]?.jobTitle || sortedData?.experience?.[0]?.title || "Jr. Full Stack Developer"}
                    </div>
                  </div>
                  <div style={{ fontSize: "4px", color: "#6b7280" }}>
                    Present
                  </div>
                </div>
              </div>
              {/* Projects */}
              <div>
                <div style={{ fontSize: "6px", fontWeight: "bold", color: "#2d3e50", textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: "1px solid #e5e7eb", paddingBottom: "2px", marginBottom: "3px" }}>
                  Projects
                </div>
                <div style={{ fontSize: "5px", fontWeight: "bold", color: "#1f2937" }}>
                  {sortedData?.projects?.[0]?.name || "PricingByCountry"}
                </div>
                <div style={{ fontSize: "4px", color: "#6b7280", fontStyle: "italic" }}>
                  React.js, Node.js, MongoDB
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      hoverable
      style={{
        cursor: "pointer",
        border: isSelected ? "2px solid #1890ff" : "1px solid #d9d9d9",
        backgroundColor: isSelected ? "#f0f9ff" : "white",
        transition: "all 0.3s ease",
      }}
      onClick={() => {
        onSelect();
        onPreview(template);
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{
            position: "relative",
            borderRadius: "8px",
            padding: "8px",
            backgroundColor: "transparent",
          }}
        >
          <div
            style={{
              height: "240px",
              borderRadius: "4px",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {renderPreviewImage()}
            {isSelected && (
              <div
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                }}
              >
                <Badge status="success" text="Selected" />
              </div>
            )}
            <div
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                color: "white",
                padding: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {isSelected ? "Selected • Click to preview" : "Click to select & preview"}
            </div>
          </div>
        </div>

        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Title level={4} style={{ margin: 0 }}>
            {template.name}
          </Title>
          <Text type="secondary">{template.description}</Text>
          <Space wrap>
            {template.features.map((feature, index) => (
              <Tag key={index} color="blue">
                {feature}
              </Tag>
            ))}
          </Space>
        </Space>
      </Space>
    </Card>
  );
};

// Main ResumePreview Component
const ReactPDF = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState(null);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const printableResumeRef = useRef();
  const customizableResumeRef = useRef();

  const templates = [
    {
      id: "classic",
      name: "Classic",
      description: "Traditional and professional design perfect for corporate positions",
      features: ["Clean Layout", "Professional", "ATS-Friendly", "Traditional"],
    },
    {
      id: "modern",
      name: "Modern",
      description: "Contemporary design with accent colors and modern typography",
      features: ["Colorful", "Two-Column", "Modern Typography", "Eye-Catching"],
    },
    {
      id: "creative",
      name: "Creative",
      description: "Unique and creative design perfect for creative industries",
      features: ["Creative Layout", "Gradients", "Unique Design", "Stand Out"],
    },
    {
      id: "simple_blush",
      name: "Simple Blush",
      description: "Clean, modern design with blush pink accents and skill progress bars",
      features: ["Blush Accents", "Skill Bars", "Two-Column", "Modern"],
    },
    {
      id: "modern_slate",
      name: "Modern Slate",
      description: "Professional design with profile photo, dark slate accents and icon grid layout",
      features: ["Profile Photo", "Icon Grid", "Dark Slate", "Professional"],
    },
    {
      id: "professional_blue",
      name: "Professional Blue",
      description: "Elegant design with centered profile photo, blue accents and clean two-column layout",
      features: ["Profile Photo", "Blue Accents", "Two-Column", "Elegant"],
    },
    {
      id: "obsidian_edge",
      name: "Obsidian Edge",
      description: "Bold dark header with professional contrast, section icons, and clean layout",
      features: ["Dark Header", "Section Icons", "Professional", "High Contrast"],
    },
    {
      id: "clean_professional",
      name: "Clean Professional",
      description: "Minimalist design with section labels on left, clean typography, and professional layout",
      features: ["Minimalist", "Section Labels", "Clean Layout", "Professional"],
    },
    {
      id: "minimalist_pro",
      name: "Minimalist Pro",
      description: "Clean modern design with teal accent bar and elegant pill-style skills",
      features: ["Teal Accent", "Pill Skills", "Modern", "Clean Layout"],
    },
    {
      id: "photo_classic",
      name: "Photo Classic",
      description: "Clean professional design with profile photo and date-aligned sections",
      features: ["Profile Photo", "Date Sidebar", "Multi-Column Skills", "References"],
    },
    {
      id: "executive_sidebar",
      name: "Executive Sidebar",
      description: "Professional two-column layout with dark blue sidebar and circular profile photo",
      features: ["Profile Photo", "Sidebar Layout", "Skill Progress Bars", "Modern"],
    },
  ];

  useEffect(() => {
    if (resumeId) {
      fetchResumeData();
    }
  }, [resumeId]);

  const fetchResumeData = async () => {
    try {
      const response = await fetch.get(`/resume/${resumeId}`);
      if (!response.success) {
        throw new Error("Failed to fetch resume data");
      }
      setResumeData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleTemplatePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewModalVisible(true);
  };

  const handlePreviewModalClose = () => {
    setPreviewModalVisible(false);
    setPreviewTemplate(null);
  };

  const handlePrintFromPreview = async () => {
    if (!previewTemplate) return;

    // Close modal immediately
    setPreviewModalVisible(false);

    // Use the CustomizableResumePreview's print method if available
    if (customizableResumeRef.current) {
      try {
        customizableResumeRef.current.handlePrint();
      } catch (err) {
        console.error("CustomizableResumePreview print error", err.message || err);
        message.error("Print failed. Please try again.");
      }
      return;
    }

    // Fallback to old print method if ref not available

    try {
      // Create a hidden iframe for silent printing/PDF generation
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.top = "-10000px";
      iframe.style.left = "-10000px";
      iframe.style.width = "8.5in";
      iframe.style.height = "11in";
      document.body.appendChild(iframe);

      // Wait for DOM to be fully updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the resume content with print CSS
      const resumeContent = document.querySelector(".printable-resume");
      if (!resumeContent) {
        throw new Error("Resume content not found");
      }

      // Clone the resume content
      const clonedContent = resumeContent.cloneNode(true);

      // Calculate content density for dynamic spacing
      const calculateContentDensity = (data) => {
        const totalItems =
          (data?.experience?.length || 0) +
          (data?.education?.length || 0) +
          (data?.projects?.length || 0) +
          (data?.certifications?.length || 0) +
          (data?.achievements?.length || 0) +
          (data?.skills ? Object.keys(data.skills).length : 0);

        const totalText = [
          data?.summary || "",
          data?.objective || "",
          ...(data?.experience || []).map((exp) => (exp.description || "").length),
          ...(data?.projects || []).map((proj) => (proj.description || "").length),
        ].join("").length;

        // Determine density based on total items and text length
        if (totalItems >= 15 || totalText > 2000) return "very-high";
        if (totalItems >= 10 || totalText > 1500) return "high";
        if (totalItems >= 6 || totalText > 1000) return "medium";
        return "low";
      };

      const contentDensity = calculateContentDensity(sortedData);

      // Add content density attribute to printable-resume div
      const printableResumeDiv = clonedContent.querySelector(".printable-resume");
      if (printableResumeDiv) {
        printableResumeDiv.setAttribute("data-content-density", contentDensity);
      }

      // Create complete HTML document with print CSS for the iframe
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Resume - ${previewTemplate.name}</title>
          <style>
            @media print, screen {
              * { box-sizing: border-box; margin: 0; padding: 0; }
              
              .printable-resume {
                font-family: ${
                  previewTemplate.id === "classic"
                    ? "Times, serif"
                    : previewTemplate.id === "modern"
                    ? "Arial, sans-serif"
                    : "Georgia, serif"
                };
                max-width: 8.5in;
                width: 100%;
                margin: 0 auto;
                padding: 0.5in;
                background: white;
                color: black;
                line-height: 1.2;
                font-size: 10pt;
                box-sizing: border-box;
                min-height: 10.5in;
                overflow: visible;
              }

              .header {
                text-align: center;
                margin-bottom: 6pt;
                border-bottom: ${
                  previewTemplate.id === "classic"
                    ? "1pt solid #333"
                    : previewTemplate.id === "modern"
                    ? "2pt solid #2563eb"
                    : "1pt solid #667eea"
                };
                padding-bottom: 2pt;
              }

              .header h1 {
                font-size: ${previewTemplate.id === "modern" ? "16pt" : "14pt"};
                font-weight: bold;
                margin: 0 0 4pt 0;
                color: ${previewTemplate.id === "modern" ? "#2563eb" : "black"};
              }

              .header .contact {
                font-size: 9pt;
                color: #666;
                margin: 0;
              }

              .section {
                margin-bottom: 4pt;
                page-break-inside: avoid;
              }

              .section-title {
                font-size: 10pt;
                font-weight: bold;
                text-transform: uppercase;
                border-bottom: 1pt solid #333;
                padding-bottom: 1pt;
                margin-bottom: 3pt;
                color: black;
              }

              .section-content {
                font-size: 9pt;
                line-height: 1.1;
              }

              .experience-item, .education-item {
                margin-bottom: 3pt;
                page-break-inside: avoid;
              }

              .experience-title, .education-title {
                font-size: 9pt;
                font-weight: bold;
                margin: 0 0 1pt 0;
                color: black;
              }

              .experience-company, .education-school {
                font-size: 9pt;
                color: black;
                margin: 0 0 1pt 0;
              }

              .experience-date, .education-date {
                font-size: 9pt;
                color: #666;
                margin: 0 0 3pt 0;
              }

              .experience-description {
                font-size: 8pt;
                line-height: 1.2;
                margin: 0;
                color: black;
              }

              .education-gpa {
                font-size: 9pt;
                color: #666;
                margin: 1pt 0 0 0;
              }

              .skills-list {
                font-size: 9pt;
                line-height: 1.1;
                margin: 0;
                display: grid !important;
                grid-template-columns: 1fr 1fr 1fr !important;
                gap: 2pt 6pt !important;
                column-gap: 6pt !important;
              }
              
              .skill-item {
                margin: 0;
                padding: 0;
                display: flex;
                align-items: flex-start;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-size: 9pt;
                line-height: 1.1;
              }
              
              .skill-item:before {
                content: "•";
                margin-right: 3pt;
                color: black;
                flex-shrink: 0;
              }

              /* Modern template specific styles */
              .modern-header {
                background: #2563eb !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .modern-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .modern-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .modern .section-title {
                color: #2563eb !important;
                border-left: 3pt solid #2563eb !important;
                border-bottom: none !important;
                padding-left: 6pt !important;
                padding-bottom: 0 !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              /* Creative template specific styles */
              .creative-header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                background-color: #667eea !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .creative-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .creative-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .creative .section-title {
                color: #667eea !important;
                border-bottom: 1pt solid #667eea !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                padding-bottom: 1pt !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              @page {
                margin: 0.5in;
                size: letter;
              }

              html, body {
                height: auto;
                width: 100%;
                margin: 0;
                padding: 0;
                overflow: visible;
              }
            }
          </style>
        </head>
        <body>
          ${clonedContent.outerHTML}
        </body>
        </html>
      `;

      // Write content to iframe
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // Wait for content to load, then trigger print with user confirmation
      setTimeout(() => {
        try {
          // Conservative approach - no navigation to avoid false positives
          let timeoutId;

          // Cleanup function
          const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);

            // Clean up iframe
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          };

          // Simple event handler - just cleanup, no navigation
          const afterPrintHandler = () => {
            // Remove event listener
            iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);

            // Cleanup after a short delay
            setTimeout(() => {
              cleanup();
            }, 500);
          };

          // Set up single event listener
          iframe.contentWindow.addEventListener("afterprint", afterPrintHandler);

          // Safety timeout - always cleanup
          timeoutId = setTimeout(() => {
            iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);
            cleanup();
          }, 30000);

          // Focus iframe and trigger print
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (printErr) {
          logger.error("Print error", {
            extras: {
              error: printErr.message || printErr,
              context: "react_pdf_print_error",
            },
          });
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          throw printErr;
        }
      }, 1000);
    } catch (err) {
      logger.error("Print error", {
        extras: {
          error: err.message || err,
          context: "react_pdf_general_print_error",
        },
      });
      message.error("Print failed. Please try again.");
    }
  };

  const handleDownloadFromPreview = async () => {
    if (!previewTemplate) return;

    setDownloadLoading(true);
    try {
      const { default: axios } = await import("axios");
      // Check for admin token first (for admin access), then regular user token
      const authToken = sessionStorage.getItem("adminResumeAccessToken") || localStorage.getItem("authorization");

      const response = await axios({
        method: "POST",
        url: `/apps/api/resume/${resumeId}/download`,
        data: {
          format: "pdf",
          template: previewTemplate.id,
        },
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken || "",
        },
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      // Extract filename from Content-Disposition header if available
      let fileName = `resume-${previewTemplate.name.toLowerCase()}.pdf`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      } else {
        // Generate filename on client side if header not available
        let firstName = resumeData?.parsedData?.personalInfo?.firstName || "";
        let lastName = resumeData?.parsedData?.personalInfo?.lastName || "";

        // If firstName or lastName not found, try the name field
        if (!firstName && !lastName && resumeData?.parsedData?.personalInfo?.name) {
          const nameParts = resumeData.parsedData.personalInfo.name
            .trim()
            .split(" ")
            .filter((part) => part);
          if (nameParts.length > 0) {
            firstName = nameParts[0];
            lastName = nameParts.slice(1).join(" ");
          }
        }

        const cleanName = (name) => name.replace(/[^a-zA-Z0-9]/g, "").trim();
        const firstNameClean = cleanName(firstName);
        const lastNameClean = cleanName(lastName);

        const templateNameMap = {
          classic_ats: "ClassicATS",
          professional: "Professional",
          executive: "Executive",
        };
        const templateName = templateNameMap[previewTemplate.id] || previewTemplate.name;

        // Build filename based on whether we have a name or not
        if (firstNameClean || lastNameClean) {
          const namePart = lastNameClean ? `${firstNameClean}_${lastNameClean}` : firstNameClean;
          fileName = `${namePart}_Resume_${templateName}_UpresumeAI.pdf`;
        } else {
          // If no name available, avoid duplicate "Resume"
          fileName = `Resume_${templateName}_UpresumeAI.pdf`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success("Resume downloaded successfully!");

      // Navigate to thank you page
      setTimeout(() => {
        navigate(`/app/resume/${resumeId}/thank-you`);
      }, 1000);
    } catch (err) {
      logger.error("Download error", {
        extras: {
          error: err.message || err,
          context: "react_pdf_download_error",
        },
      });
      message.error("Download failed. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleBackToChat = () => {
    navigate(`/app/resume/${resumeId}`);
  };

  const handleLinkedInShare = () => {
    const shareUrl = "https://www.upresume.ai/"; // Live production URL
    const shareText = `🚀 Just updated my resume in minutes - completely FREE!

✨ What makes upresume.ai amazing:
• Upload your existing resume - NO sign up required!
• No hassle of sign in - Start immediately 
• Use everything totally FREE
• Just upload → make changes → download updated resume
• AI-powered improvements & ATS optimization

Perfect solution for quick resume updates without any barriers! 💼

Stop wasting time with complicated tools. Try the simplest way to enhance your resume.`;

    // Use LinkedIn's sharing URL with auto-compose
    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
      shareText + "\n\n" + shareUrl
    )}`;

    // Open LinkedIn and attempt auto-posting
    const popup = window.open(
      linkedInUrl,
      "_blank",
      "width=650,height=700,scrollbars=yes,resizable=yes,toolbar=no,menubar=no"
    );

    // Focus on the popup to ensure it's visible
    if (popup) {
      popup.focus();

      // Optional: Auto-close after posting (if needed)
      setTimeout(() => {
        if (popup && !popup.closed) {
          // You can add logic here to detect when post is made
        }
      }, 2000);
    } else {
      // Fallback if popup is blocked

      // Copy text to clipboard and show message
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText + "\n\n" + shareUrl).then(() => {
          message.info("Content copied to clipboard! Please paste it on LinkedIn.");
          // Open LinkedIn in same tab as fallback
          window.open("https://www.linkedin.com/feed/", "_blank");
        });
      }
    }
  };

  const handlePrint = () => {
    // Call the PrintableResume component's handlePrint function
    if (printableResumeRef.current) {
      printableResumeRef.current.handlePrint();
    } else {
      logger.error("printableResumeRef is not available", {
        extras: {
          context: "react_pdf_printable_ref_missing",
        },
      });
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Resume Preview</Title>
            <Card>
              <Skeleton active paragraph={{ rows: 10 }} />
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  if (error || !resumeData) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Resume Preview</Title>
            <Card>
              <Empty
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                description={
                  <div>
                    <Title level={4}>Resume not found</Title>
                    <Text>The resume could not be loaded. Please try uploading a new one.</Text>
                  </div>
                }
              />
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  // Show thank you message if showThankYou is true
  if (false) {
    // Disabled - now using dedicated thank you page
    return (
      <Layout>
        <Content style={{ padding: "24px", minHeight: "100vh" }}>
          <div
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              paddingTop: "60px",
            }}
          >
            <Result
              icon={<CheckCircleOutlined style={{ color: "#52c41a", fontSize: "72px" }} />}
              title={
                <Title level={2} style={{ color: "#52c41a", marginBottom: "16px" }}>
                  Thank You for Using Our AI Resume Builder!
                </Title>
              }
              subTitle={
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%", maxWidth: "500px", margin: "0 auto" }}
                >
                  <Typography.Paragraph
                    style={{
                      fontSize: "16px",
                      color: "#595959",
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    Your professional resume has been successfully created and downloaded to your device.
                  </Typography.Paragraph>

                  <Card
                    size="small"
                    style={{
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Space direction="vertical" size="middle">
                      <Typography.Title level={4} style={{ color: "#1890ff", margin: 0 }}>
                        Your Resume is Ready!
                      </Typography.Title>
                      <Typography.Text style={{ color: "#8c8c8c" }}>
                        The PDF has been optimized for ATS systems and is ready for job applications.
                      </Typography.Text>
                    </Space>
                  </Card>

                  <Card
                    size="small"
                    style={{
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                      marginTop: "16px",
                    }}
                  >
                    <Space direction="vertical" size="middle">
                      <Typography.Title level={4} style={{ color: "#0077b5", margin: 0 }}>
                        <ShareAltOutlined style={{ marginRight: "8px" }} />
                        Share Your Success
                      </Typography.Title>
                      <Typography.Text style={{ color: "#8c8c8c", marginBottom: "12px" }}>
                        Let your network know about your professional achievement!
                      </Typography.Text>
                      <Button
                        type="primary"
                        icon={<LinkedinOutlined />}
                        onClick={handleLinkedInShare}
                        style={{
                          backgroundColor: "#0077b5",
                          borderColor: "#0077b5",
                          borderRadius: "6px",
                        }}
                        size="large"
                      >
                        Share on LinkedIn
                      </Button>
                    </Space>
                  </Card>
                </Space>
              }
              extra={
                <Space size="middle">
                  <Button type="default" icon={<HomeOutlined />} onClick={handleGoHome} size="large">
                    Dashboard
                  </Button>
                  <Button type="primary" icon={<EditOutlined />} onClick={handleCreateAnother} size="large">
                    Create New Resume
                  </Button>
                </Space>
              }
            />

            <div style={{ textAlign: "center", marginTop: "32px", paddingBottom: "40px" }}>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Best of luck with your job applications!
              </Typography.Text>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <AppLayout>
      <Layout
        style={{
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Content
          style={{
            padding: "40px 24px 24px 24px",
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%", marginTop: "32px" }}>
            {/* Template Selection */}
            <Card>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div style={{ textAlign: "center" }}>
                  <Title level={3}>Choose Your Resume Template(from React PDF)</Title>
                  <Text type="secondary">Select a template that best represents your professional style</Text>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "20px",
                  }}
                >
                  {templates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      isSelected={selectedTemplate?.id === template.id}
                      onSelect={() => handleTemplateSelect(template)}
                      onPreview={handleTemplatePreview}
                      resumeData={resumeData}
                    />
                  ))}
                </div>
              </Space>
            </Card>
          </Space>

          {/* Hidden printable content for main print function */}
          {selectedTemplate && resumeData && (
            <div style={{ display: "none" }}>
              <PrintableResume
                ref={printableResumeRef}
                resumeData={resumeData}
                selectedTemplate={selectedTemplate}
                navigate={navigate}
                customSectionOrder={loadCustomSectionOrder(resumeId)}
                onDownloadComplete={() => {
                  /* Navigation handled in PrintableResume */
                }}
              />
            </div>
          )}

          {/* Preview Modal */}
          <Modal
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{previewTemplate?.name} Template Preview</span>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={handlePreviewModalClose}
                  style={{ border: "none", boxShadow: "none" }}
                />
              </div>
            }
            open={previewModalVisible}
            onCancel={handlePreviewModalClose}
            footer={[
              <Button key="close" onClick={handlePreviewModalClose}>
                Close
              </Button>,
              <Button
                key="print"
                icon={<PrinterOutlined />}
                onClick={handlePrintFromPreview}
                loading={downloadLoading}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "white" }}
              >
                Download Resume
              </Button>,
            ]}
            width="95%"
            style={{ top: 10 }}
            bodyStyle={{
              maxHeight: "85vh",
              overflowY: "auto",
              padding: "20px",
              backgroundColor: "#f5f5f5",
            }}
            closable={false} // We're using custom close button in title
          >
            {previewTemplate && resumeData && (
              <CustomizableResumePreview
                ref={customizableResumeRef}
                resumeData={resumeData}
                selectedTemplate={previewTemplate}
                resumeId={resumeId}
                onClose={handlePreviewModalClose}
                onPrint={handlePrintFromPreview}
              />
            )}
          </Modal>
        </Content>
      </Layout>
    </AppLayout>
  );
};

export default ReactPDF;
